// ./src/pages/private/SubmissionsPage/SubmissionsPage.tsx
import React, { useState, useCallback, useEffect } from "react";
import { Stack, Alert } from "@mantine/core";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  //SearchInput,
  AppIcon,
  LoadingDataBlock,
  SortConfig,
} from "@/components/generic";
import {
  SubmissionsTable,
  SubmissionDetailsDrawer,
  FormSelector,
  SubmissionsFilters,
} from "@/components/features";
import { useSubmissions, useForms } from "@/hooks";
import { Submission, Form, SubmissionFilter } from "@/utils/api";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";

export const SubmissionsPage: React.FC<PageProps> = (props: PageProps) => {
  const { isAuthenticated } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ submissionId?: string }>();

  // Parse URL to determine which modal/drawer to show
  const submissionIdFromUrl = params.submissionId;
  const isViewMode =
    !!submissionIdFromUrl && location.pathname.endsWith("/view");

  // Local UI state
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [selectedForm, setSelectedForm] = useState<Form | null>(null);
  const [submissionToDelete, setSubmissionToDelete] =
    useState<Submission | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");

  // Track the submission being viewed in the drawer (with full details)
  const [drawerSubmission, setDrawerSubmission] = useState<Submission | null>(
    null
  );
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Use forms hook to get form list and details
  const {
    form,
    forms,
    loading: formsLoading,
    isEmpty: noFormsExist,
    get: getForm,
  } = useForms({
    autoLoad: true,
    isAuthenticated,
  });

  // Use submissions hook for data management
  const {
    // Data state
    submission: hookSubmission,
    submissions,
    loading: submissionsLoading,
    loadingMore,
    hasMore,
    isEmpty: noSubmissions,

    // Search and filters - use these as source of truth
    filters,
    sortBy,

    // Operations state
    removingSubmissionId,

    // Data operations
    list,
    loadMore,
    get: getSubmission,
    update,

    // CRUD operations
    remove,

    // Error handling
    clearError,
  } = useSubmissions({
    autoLoad: false,
    isAuthenticated,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Auto-select first form when forms are loaded
  useEffect(() => {
    if (!formsLoading && forms.length > 0 && !selectedFormId) {
      const firstForm = forms[0];
      if (firstForm.id) {
        setSelectedFormId(firstForm.id);
        setSelectedForm(firstForm);
      }
    }
  }, [formsLoading, forms, selectedFormId]);

  // Load form details and submissions when form is selected
  useEffect(() => {
    if (selectedFormId) {
      // Load form details to get fields
      getForm(selectedFormId);

      // Load submissions for this form with fresh filters
      list(
        { formId: [selectedFormId] },
        { page: 1, pageSize: DEFAULT_PAGE_SIZE },
        sortBy,
        true
      );
    }
  }, [selectedFormId]);

  // Update selectedForm when form details are loaded from useForms hook
  useEffect(() => {
    if (form && form.id === selectedFormId) {
      setSelectedForm(form);
    }
  }, [form, selectedFormId]);

  // Load submission details when drawer opens
  useEffect(() => {
    if (isViewMode && submissionIdFromUrl) {
      setDrawerLoading(true);
      getSubmission(submissionIdFromUrl);
    } else {
      setDrawerSubmission(null);
    }
  }, [isViewMode, submissionIdFromUrl, getSubmission]);

  // Sync drawer submission when hook's submission updates (after get completes)
  useEffect(() => {
    if (hookSubmission && hookSubmission.id === submissionIdFromUrl) {
      setDrawerSubmission(hookSubmission);
      setDrawerLoading(false);
    }
  }, [hookSubmission, submissionIdFromUrl]);

  // Keep drawer submission in sync when submissions list updates (e.g., after status change)
  useEffect(() => {
    if (drawerSubmission && submissionIdFromUrl) {
      const updated = submissions.find((s) => s.id === submissionIdFromUrl);
      if (updated && updated.status !== drawerSubmission.status) {
        // Merge: update status but keep files from drawerSubmission
        setDrawerSubmission((prev) =>
          prev
            ? {
                ...prev,
                status: updated.status,
              }
            : prev
        );
      }
    }
  }, [submissions, submissionIdFromUrl, drawerSubmission]);

  // Handle form selection change
  const handleFormChange = useCallback(
    (formId: string | null, form?: Form) => {
      setSelectedFormId(formId);
      setSelectedForm(form || null);
      setSearchValue("");
      clearError();
    },
    [clearError]
  );

  // Handle filter changes from SubmissionsFilters component
  const handleFiltersChange = useCallback(
    (newFilters: Partial<SubmissionFilter>) => {
      if (!selectedFormId) return;

      // Merge new filters with existing filters, preserving formId and searchKeyword
      const combinedFilters: Partial<SubmissionFilter> = {
        ...newFilters,
        formId: [selectedFormId],
      };

      // Preserve search keyword if it exists
      if (searchValue.trim()) {
        combinedFilters.searchKeyword = searchValue.trim();
      }

      list(
        combinedFilters,
        { page: 1, pageSize: DEFAULT_PAGE_SIZE },
        sortBy,
        true
      );
    },
    [selectedFormId, searchValue, list, sortBy]
  );

  // Handle search input change
  // const handleSearchChange = useCallback(
  //   (value: string) => {
  //     setSearchValue(value);

  //     if (!selectedFormId) return;

  //     // Merge search with existing filters
  //     const combinedFilters: Partial<SubmissionFilter> = {
  //       ...filters,
  //       formId: [selectedFormId],
  //     };

  //     if (value.trim()) {
  //       combinedFilters.searchKeyword = value.trim();
  //     } else {
  //       delete combinedFilters.searchKeyword;
  //     }

  //     list(
  //       combinedFilters,
  //       { page: 1, pageSize: DEFAULT_PAGE_SIZE },
  //       sortBy,
  //       true
  //     );
  //   },
  //   [selectedFormId, filters, list, sortBy]
  // );

  const handleSortBy = useCallback(
    (value: SortConfig[]) => {
      list(filters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, value, true);
    },
    [filters, list]
  );

  // Handle view submission
  const handleViewSubmission = useCallback(
    (submission: Submission) => {
      clearError();
      if (submission.id) {
        navigate(`/submissions/${submission.id}/view`);
      }
    },
    [navigate, clearError]
  );

  // Handle remove submission button click
  const handleRemoveSubmissionClick = useCallback((submission: Submission) => {
    setSubmissionToDelete(submission);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (submissionToDelete?.id) {
      const success = await remove(submissionToDelete.id);
      if (success) {
        setSubmissionToDelete(null);
        // If we're currently viewing this submission, close the drawer
        if (submissionIdFromUrl === submissionToDelete.id) {
          navigate("/submissions");
        }
      }
    }
  }, [submissionToDelete, remove, submissionIdFromUrl, navigate]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setSubmissionToDelete(null);
  }, []);

  // Handle submission details drawer close
  const handleDetailsDrawerClose = useCallback(() => {
    clearError();
    setDrawerSubmission(null);
    navigate("/submissions");
  }, [navigate, clearError]);

  // Handle status change from details drawer
  const handleStatusChanged = useCallback(
    async (newStatus: number) => {
      if (submissionIdFromUrl && drawerSubmission) {
        // Optimistically update the drawer submission
        setDrawerSubmission((prev) =>
          prev ? { ...prev, status: newStatus } : prev
        );

        // Update on the server (this also updates the submissions list)
        await update(submissionIdFromUrl, { status: newStatus });
      }
    },
    [submissionIdFromUrl, drawerSubmission, update]
  );

  // Handle refresh - uses current filters and sortBy from hook
  const handleRefresh = useCallback(() => {
    if (selectedFormId) {
      list(filters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    }
  }, [selectedFormId, filters, sortBy, list]);

  // Determine if we can create submissions (form has fields)
  //const canCreate = hasRightTo(ENTITIES.SUBMISSIONS, OPERATION_RIGHTS.CREATE);
  const canCreateSubmissions =
    selectedForm &&
    selectedForm.formFields &&
    selectedForm.formFields.length > 0;

  // Determine current UI state
  const isLoadingForms = formsLoading && forms.length === 0;
  const isLoadingSubmissions = submissionsLoading && submissions.length === 0;

  // Check if any filters are active (for empty state messaging)
  const hasActiveFilters =
    filters.status !== undefined ||
    filters.hasFiles !== undefined ||
    filters.fromDate !== undefined ||
    filters.toDate !== undefined ||
    searchValue.trim() !== "";

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with form selector, actions and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "form-selector",
              items: [
                <FormSelector
                  key="form-selector"
                  value={selectedFormId}
                  onChange={handleFormChange}
                  placeholder="Select a form to view submissions"
                  style={{ minWidth: 250 }}
                  required
                  disabled={isLoadingForms}
                />,
                <SubmissionsFilters
                  key="filters"
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  disabled={!selectedFormId || isLoadingForms}
                  size="md"
                />,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={
                    submissionsLoading || !selectedFormId || isLoadingForms
                  }
                ></AppButton>,
              ],
              align: "left",
              gap: "sm",
            },
            // {
            //   id: "search",
            //   items: [
            //     <SearchInput
            //       key="search"
            //       placeholder="Search submissions..."
            //       value={searchValue}
            //       onChange={handleSearchChange}
            //       style={{ minWidth: 250 }}
            //       disabled={!selectedFormId || isLoadingForms}
            //     />,
            //   ],
            //   align: "right",
            // },
          ]}
        />

        {/* Loading forms state */}
        {isLoadingForms && <LoadingDataBlock text="Loading forms..." />}

        {/* No forms exist state */}
        {!isLoadingForms && noFormsExist && (
          <EmptyState
            display={true}
            title="No forms available"
            description="You need to create a form first before you can view submissions"
            iconName="FileText"
            actionLabel="Create Form"
            actionIcon="Plus"
            onAction={() => navigate("/forms")}
          />
        )}

        {/* Form has no fields warning */}
        {!formsLoading &&
          !isLoadingForms &&
          !noFormsExist &&
          selectedFormId &&
          selectedForm &&
          !canCreateSubmissions && (
            <Alert
              icon={<AppIcon iconName="AlertTriangle" size={16} />}
              color="yellow"
              variant="light"
            >
              This form has no configured fields. Manual submission creation is
              disabled. You can still view submissions received from external
              sources.
            </Alert>
          )}

        {/* Loading submissions state */}
        {!isLoadingForms &&
          !noFormsExist &&
          selectedFormId &&
          isLoadingSubmissions && (
            <LoadingDataBlock text="Loading submissions..." />
          )}

        {/* Submissions table - only show when form is selected and not loading */}
        {!isLoadingForms &&
          !noFormsExist &&
          selectedFormId &&
          !isLoadingSubmissions && (
            <SubmissionsTable
              submissions={submissions}
              loading={submissionsLoading}
              loadingMore={loadingMore}
              hasMore={hasMore}
              sortBy={sortBy}
              onSortChange={handleSortBy}
              onLoadMore={loadMore}
              onViewSubmission={handleViewSubmission}
              onRemoveSubmission={handleRemoveSubmissionClick}
              removingSubmissionId={removingSubmissionId}
              formFields={form?.formFields}
              form={selectedForm}
              onRowClick={handleViewSubmission}
              size="md"
            />
          )}

        {/* Empty state when form selected but no submissions */}
        {!isLoadingForms &&
          !noFormsExist &&
          selectedFormId &&
          !isLoadingSubmissions &&
          noSubmissions && (
            <EmptyState
              display={true}
              title={
                hasActiveFilters
                  ? "No submissions match your filters"
                  : "No submissions yet"
              }
              description={
                hasActiveFilters
                  ? "Try adjusting your filters or search criteria to find what you're looking for."
                  : "This form hasn't received any submissions yet. Learn how to integrate it on your website."
              }
              iconName={hasActiveFilters ? "FilterOff" : "Inbox"}
              actionLabel={hasActiveFilters ? undefined : "View Documentation"}
              actionIcon={hasActiveFilters ? undefined : "Book"}
              onAction={hasActiveFilters ? undefined : () => navigate("/docs")}
            />
          )}
      </Stack>

      {/* Submission details drawer */}
      <SubmissionDetailsDrawer
        opened={isViewMode}
        onClose={handleDetailsDrawerClose}
        submission={drawerSubmission}
        loading={drawerLoading}
        form={selectedForm}
        formFields={form?.formFields}
        onStatusChanged={handleStatusChanged}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={!!submissionToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Submission"
        message={`Are you sure you want to delete this submission?`}
        details="This action cannot be undone. The submission data will be permanently removed."
        confirmInProgress={removingSubmissionId === submissionToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
